/**
 * buildTemplateLoadPayload
 *
 * Shared "New Estimate from this template" logic: take a saved estimate's
 * wizard config, strip the original creator's personal info + project name,
 * mark every step complete so the user can navigate freely, and land them on
 * the last step before Calculate Estimate. Used by both the Header (shared
 * estimate view) and the Account "My estimates" list.
 */

import type { LoadStatePayload } from '../context/EstimatorContext';
import type { WizardState, WizardStep, Currency } from '../types';

/** Last step before the Calculate Estimate action for each mode. */
const FINAL_PRE_CALC_STEP: Record<string, WizardStep> = {
  FullRoom: 'application',
  CeilingOnly: 'ceiling',
  IndividualWalls: 'application',
};

const STEP_ORDER_BY_MODE: Record<string, WizardStep[]> = {
  FullRoom: ['mode', 'walls', 'ceiling', 'dimensions', 'application'],
  CeilingOnly: ['mode', 'ceiling'],
  IndividualWalls: ['mode', 'walls', 'ceiling', 'application'],
};

export function buildTemplateLoadPayload(
  // WizardState's public type doesn't declare `currency`, but the payload saved
  // to the DB carries it — treat it as an optional add-on rather than widening
  // the public type.
  wizardState: WizardState & { currency?: Currency },
): LoadStatePayload {
  const mode = wizardState.calculationMode;
  const stepOrder = STEP_ORDER_BY_MODE[mode] ?? STEP_ORDER_BY_MODE.FullRoom;
  const targetStep = FINAL_PRE_CALC_STEP[mode] ?? stepOrder[stepOrder.length - 1];

  return {
    calculationMode: wizardState.calculationMode,
    projectInfo: {
      projectName: '',
      customerName: '',
      email: '',
      phone: '',
      location: '',
    },
    wallsConfig: wizardState.wallsConfig,
    ceilingConfig: wizardState.ceilingConfig,
    dimensionsConfig: wizardState.dimensionsConfig,
    applicationConfig: wizardState.applicationConfig,
    individualWalls: wizardState.individualWalls,
    currency: wizardState.currency ?? 'USD',
    completedSteps: [...stepOrder],
    currentStep: targetStep,
  };
}
